import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, 
  Typography, 
  Input, 
  Button, 
  message, 
  Space, 
  Divider,
  Tooltip,
  Alert,
  Skeleton,
  Modal
} from 'antd';
import { 
  SettingOutlined, 
  CopyOutlined, 
  EyeOutlined, 
  EyeInvisibleOutlined,
  KeyOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { generateApiKey, getUserProfile, clearMessages } from '../features/user/userSlice';
import { MaintenanceToggle, GmailSmtpSettings } from '../components';

const { Title, Text, Paragraph } = Typography;

const Settings = () => {
  const dispatch = useDispatch();
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Get state from both auth and user slices
  const { user } = useSelector((state) => state.auth);
  const { 
    profile, 
    apiKey, 
    apiKeyLoading, 
    profileLoading, 
    error, 
    success,
    lastApiKeyGenerated 
  } = useSelector((state) => state.user);

  // Use the API key from user slice if available, otherwise fall back to auth user
  const currentApiKey = apiKey || profile?.apiKey || user?.apiKey;
  const loading = profileLoading;

  // Fetch user profile on component mount
  useEffect(() => {
    dispatch(getUserProfile());
  }, [dispatch]);

  // Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearMessages());
    };
  }, [dispatch]);

  // Show success/error messages
  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearMessages());
    }
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [success, error, dispatch]);

  const handleCopyApiKey = async () => {
    if (!currentApiKey) {
      message.error('No API key available to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(currentApiKey);
      setCopied(true);
      message.success('API key copied to clipboard!');
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy API key:', error);
      message.error('Failed to copy API key. Please try selecting and copying manually.');
    }
  };

  const toggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    return `${key.slice(0, 7)}${'*'.repeat(32)}${key.slice(-7)}`;
  };

  const handleRegenerateApiKey = () => {
    Modal.confirm({
      title: 'Regenerate API Key',
      content: 'Are you sure you want to regenerate your API key? This will invalidate your current API key and any applications using it will need to be updated.',
      okText: 'Yes, Regenerate',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        dispatch(generateApiKey());
      },
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="shadow-soft border border-border-light rounded-xl">
          <Skeleton active />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
            <SettingOutlined className="text-xl text-white" />
          </div>
          <div>
            <Title level={2} className="mb-1">
              Settings
            </Title>
            <Paragraph className="text-gray-600 mb-0">
              Manage your account settings and preferences
            </Paragraph>
          </div>
        </div>
      </Card>

      {/* Maintenance Mode Toggle */}
      <MaintenanceToggle />

      {/* API Key Management */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="space-y-6">
          <div className="flex items-center space-x-3">
            <KeyOutlined className="text-xl text-blue-600" />
            <Title level={3} className="mb-0">
              API Key Management
            </Title>
          </div>

          <Alert
            message="Keep your API key secure"
            description="Your API key provides access to your account. Don't share it with others or expose it in client-side code."
            type="warning"
            showIcon
            className="mb-4"
          />

          {!currentApiKey && (
            <Alert
              message="No API Key Found"
              description="Generate an API key to access our services programmatically."
              type="info"
              showIcon
              className="mb-4"
            />
          )}

          {lastApiKeyGenerated && (
            <Alert
              message="API Key Recently Generated"
              description={`New API key was generated on ${new Date(lastApiKeyGenerated).toLocaleString()}`}
              type="success"
              showIcon
              className="mb-4"
            />
          )}

          <div className="space-y-4">
            {currentApiKey ? (
              <div>
                <Text strong className="block mb-2">
                  Your API Key
                </Text>
                <div className="flex items-center space-x-2">
                  <Input
                    value={showApiKey ? currentApiKey : maskApiKey(currentApiKey)}
                    readOnly
                    className="font-mono text-sm"
                    style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace' }}
                  />
                  
                  <Tooltip title={showApiKey ? "Hide API key" : "Show API key"}>
                    <Button
                      icon={showApiKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={toggleApiKeyVisibility}
                      type="default"
                    />
                  </Tooltip>
                  
                  <Tooltip title="Copy API key">
                    <Button
                      icon={<CopyOutlined />}
                      onClick={handleCopyApiKey}
                      type={copied ? "primary" : "default"}
                      className={copied ? "bg-green-500 border-green-500" : ""}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                  </Tooltip>
                </div>
              </div>
            ) : (
              <div>
                <Text strong className="block mb-2">
                  Generate Your First API Key
                </Text>
                <Button
                  icon={<KeyOutlined />}
                  onClick={() => dispatch(generateApiKey())}
                  loading={apiKeyLoading}
                  type="primary"
                  size="large"
                >
                  {apiKeyLoading ? 'Generating...' : 'Generate API Key'}
                </Button>
              </div>
            )}

            {currentApiKey && (
              <>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <Text strong className="block mb-2">
                    How to use your API key:
                  </Text>
                  <div className="space-y-2">
                    <div className="bg-gray-800 text-white p-3 rounded font-mono text-sm overflow-x-auto">
                      <div className="text-green-400"># Using curl</div>
                      <div>curl -H "X-API-Key: {currentApiKey.slice(0, 10)}..." https://your-api.com/endpoint</div>
                    </div>
                    <div className="bg-gray-800 text-white p-3 rounded font-mono text-sm overflow-x-auto">
                      <div className="text-green-400"># Using JavaScript</div>
                      <div>fetch('https://your-api.com/endpoint', {`{`}</div>
                      <div>{'  '}headers: {`{`} 'X-API-Key': '{currentApiKey.slice(0, 10)}...' {`}`}</div>
                      <div>{`})`}</div>
                    </div>
                  </div>
                </div>

                <Divider />

                <div className="space-y-3">
                  <Text strong className="block">
                    API Key Actions
                  </Text>
                  <Space>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleRegenerateApiKey}
                      loading={apiKeyLoading}
                      danger
                    >
                      {apiKeyLoading ? 'Regenerating...' : 'Regenerate API Key'}
                    </Button>
                    <Text type="secondary" className="text-sm">
                      This will invalidate your current API key
                    </Text>
                  </Space>
                </div>
              </>
            )}

            <Alert
              message="Usage Information"
              description={
                <div className="space-y-1">
                  <div>• Current plan: {profile?.role || user?.role || 'Unknown'}</div>
                  <div>• Email: {profile?.email || user?.email || 'Not available'}</div>
                  <div>• Account created: {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Unknown'}</div>
                  {profile?.apiKeyGeneratedAt && (
                    <div>• API key generated: {new Date(profile.apiKeyGeneratedAt).toLocaleString()}</div>
                  )}
                </div>
              }
              type="info"
              showIcon
            />
          </div>
        </div>
      </Card>

      {/* Gmail SMTP Settings */}
      <GmailSmtpSettings />

      {/* Additional Settings - Placeholder for future features */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="space-y-4">
          <Title level={3}>
            Additional Settings
          </Title>
          <Text type="secondary">
            More settings and preferences will be available here in future updates.
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Settings;